import { useState, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'gatsby-plugin-react-intl';
import { themeGet } from '@styled-system/theme-get';
import uuid from 'react-uuid';
import ReactHtmlParser from 'react-html-parser';

import Container from '../../Core/Container';
import Map from '../Map';

import {
  MapContainer,
  Wrapper,
  Controls,
  Search,
  LocationButton,
  MapResults,
  ResultAccordion,
  AccordionButton,
  ResultContent,
  ResultCollection,
  ResultDistance,
  ResultType,
  ResultLocation,
  AccordionBody,
  ErrorText,
} from './styles';

let autocomplete;
let geocoder;
let infoWindow;
let service;

// * Improve styling for the distances in the accordion button

const markerLabels = 'ABCDEF';

const markerInfo = ({ Distance, LocationType, FullAddress, hours }, lang) => {
  return `
  <div>
  <div class="gm-style marker-wrapper">
  <div class="marker-group">
    <div class="marker-info">
      <div class="marker-type">${LocationType[lang]}</div>
      <div class="marker-distance">${Distance}km</div>
    </div>
    <div class="marker-address">${FullAddress}</div>
  </div>
  <div class="marker-hours">
    <ul>
      <li><span class="marker-day">${
        lang === 'en' ? 'M' : 'L'
      }</span><span class="marker-time">${hours.Monday}</span></li>
      <li><span class="marker-day">${
        lang === 'en' ? 'T' : 'M'
      }</span><span class="marker-time">${hours.Tuesday}</span></li>
      <li><span class="marker-day">${
        lang === 'en' ? 'W' : 'M'
      }</span><span class="marker-time">${hours.Wednesday}</span></li>
      <li><span class="marker-day">${
        lang === 'en' ? 'T' : 'J'
      }</span><span class="marker-time">${hours.Thursday}</span></li>
      <li><span class="marker-day">${
        lang === 'en' ? 'F' : 'V'
      }</span><span class="marker-time">${hours.Friday}</span></li>
      <li><span class="marker-day">${
        lang === 'en' ? 'S' : 'S'
      }</span><span class="marker-time">${hours.Saturday}</span></li>
      <li><span class="marker-day">${
        lang === 'en' ? 'S' : 'D'
      }</span><span class="marker-time">${hours.Sunday}</span></li>
    </ul>
  </div>
  </div>
</div>
    `;
};

const ABMAccordion = ({ data, lang, char = '' }) => {
  if (!data) return null;

  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const { Distance, LocationType, FullAddress, hours } = data;
  const days = Object.keys(hours);
  const accordionAriaLabel =
    intl.locale === 'en'
      ? `Expand for ${LocationType.en} hours of operation at ${FullAddress}`
      : `Étendre ${LocationType.fr} pour heure d’opération à ${FullAddress}`;

  return (
    <ResultAccordion>
      <AccordionButton
        onClick={() => setOpen(!open)}
        open={open}
        char={char}
        aria-expanded={open}
        aria-label={accordionAriaLabel}
      >
        <ResultContent>
          <ResultCollection>
            <ResultDistance>
              <strong>{Distance}km</strong>
            </ResultDistance>
            <ResultType>
              <strong>{LocationType[lang]}</strong>
            </ResultType>
          </ResultCollection>
          <ResultLocation>{FullAddress}</ResultLocation>
        </ResultContent>
      </AccordionButton>
      <AccordionBody open={open}>
        <ul className="no-bullets">
          {days.map(day => {
            return (
              <li key={uuid()}>
                <strong>
                  <FormattedMessage id={day} />
                </strong>
                <span>{hours[day]}</span>
              </li>
            );
          })}
        </ul>
      </AccordionBody>
    </ResultAccordion>
  );
};

const ABMLocator = ({ data }) => {
  const intl = useIntl();
  if (!data) return null;
  const {
    mapHeading,
    mapSubheading,
    inputField,
    inputLabel,
    locationData,
    component,
  } = data;

  const [mapInstance, setMapInstance] = useState(null);

  const [input, setInput] = useState('');
  const inputRef = createRef();

  const [zoom, setZoom] = useState(5);
  const [location, setLocation] = useState({
    lat: 54.4159862,
    lng: -110.17902,
  });

  const [closestLocations, setClosestLocations] = useState([]);

  const [error, setError] = useState(false);

  const onMarkerClick = (info, marker) => {
    if (!infoWindow) return;
    infoWindow.setContent(markerInfo(info, intl.locale));
    infoWindow.open(mapInstance, marker);
  };

  const renderMarkers = (map, closestMarkers) => {
    locationData.forEach(poi => {
      const labelText =
        closestMarkers.indexOf(poi) > -1
          ? markerLabels[closestMarkers.indexOf(poi)]
          : ' ';
      const coords = new window.google.maps.LatLng({
        lat: poi.Lat,
        lng: poi.Lng,
      });
      const marker = new window.google.maps.Marker({
        position: coords,
        // animation: window.google.maps.Animation.DROP,
        label: {
          text: `${labelText}`,
          color: `${themeGet('colors.white')}`,
          fontSize: '16px',
          fontWeight: 'bold',
        },
        map,
      });
      marker.addListener('click', () => onMarkerClick(poi, marker));
      return poi;
    });
    const coords = new window.google.maps.LatLng(location);
    const marker = new window.google.maps.Marker({
      position: coords,
      map,
    });
    return marker;
  };

  const updateMap = (position, scale = 15) => {
    setLocation(position);
    setZoom(scale);
  };

  const handlePlaceSelect = () => {
    const place = autocomplete.getPlace();
    if (place && place.geometry) {
      setError(false);
      setInput(place.formatted_address);
      updateMap({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });
    }
  };

  const onLoad = (map, maps) => {
    setMapInstance(map);
    geocoder = new maps.Geocoder();
    service = new maps.DistanceMatrixService();
    infoWindow = new maps.InfoWindow({
      content: '',
    });
  };

  const getUserLocation = e => {
    e.preventDefault();
    if (typeof window !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        updateMap({ lat, lng });
      });
    } else {
      console.error('geolocation is not available');
    }
  };

  const getCoordinates = e => {
    e.preventDefault();
    if (geocoder && input.length >= 1) {
      const query = input.split(' ').join('+');
      geocoder.geocode({ address: query }, (res, status) => {
        if (status === 'OK') {
          setError(false);
          const lat = res[0].geometry.location.lat();
          const lng = res[0].geometry.location.lng();
          updateMap({ lat, lng });
        } else {
          setError(true);
          // console.error(
          //   `Geocode was not successful for the following reason: ${status}`,
          // );
        }
      });
    }
  };

  const calcDistance = (to, from = location) => {
    const center = new window.google.maps.LatLng(from);
    const distance =
      window.google.maps.geometry.spherical.computeDistanceBetween(center, to);
    return distance;
  };

  const fallBackDistance = (markers, map) => {
    const places = markers.filter((_, index) => index <= 4);
    setClosestLocations(places);
    renderMarkers(map, places);
  };

  useEffect(() => {
    const options = { types: ['geocode'] };
    if (mapInstance) {
      autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        options,
      );
      autocomplete.addListener('place_changed', handlePlaceSelect);
    }
  }, [mapInstance]);

  useEffect(() => {
    if (mapInstance) {
      // * Determines distance using haversine
      // const closeLocations = locationData
      //   .map(el => {
      //     const coords = new window.google.maps.LatLng({
      //       lat: el.Lat,
      //       lng: el.Lng,
      //     });
      //     el.Distance =
      //       Math.round((calcDistance(coords) / 1000) * 100 + Number.EPSILON) /
      //       100;
      //     return el;
      //   })
      //   .sort((a, b) => a.Distance - b.Distance)
      //   .filter((_, index) => index <= 4);
      // setClosestLocations(closeLocations);
      // renderMarkers(mapInstance, closeLocations);
      // * Determines distance using google maps driving calc
      const closeLocations = locationData
        .map(el => {
          const coords = new window.google.maps.LatLng({
            lat: el.Lat,
            lng: el.Lng,
          });
          el.Distance =
            Math.round((calcDistance(coords) / 1000) * 100 + Number.EPSILON) /
            100;
          return el;
        })
        .sort((a, b) => a.Distance - b.Distance)
        .filter((_, index) => index <= 24);
      const locationsToCalc = closeLocations.map(el => el.FullAddress);
      const origin = new window.google.maps.LatLng(location);
      if (service) {
        service.getDistanceMatrix(
          {
            origins: [origin],
            destinations: locationsToCalc,
            travelMode: 'DRIVING',
          },
          (response, status) => {
            if (
              status === 'OK' &&
              response.rows[0].elements[0].status === 'OK'
            ) {
              const calcLocations = closeLocations
                .map((place, index) => {
                  place.Distance =
                    Math.round(
                      (response.rows[0].elements[index].distance.value / 1000) *
                        100 +
                        Number.EPSILON,
                    ) / 100;
                  return place;
                })
                .sort((a, b) => a.Distance - b.Distance)
                .filter((_, index) => index <= 4);
              setClosestLocations(calcLocations);
              renderMarkers(mapInstance, calcLocations);
            } else {
              fallBackDistance(closeLocations, mapInstance);
            }
          },
        );
      } else {
        fallBackDistance(closeLocations, mapInstance);
      }
    }
  }, [location]);

  return (
    <Container backgroundColor="stone" className={component.toLowerCase()}>
      <Wrapper>
        <h2>{ReactHtmlParser(mapHeading)}</h2>
        <div>{ReactHtmlParser(mapSubheading.childMarkdownRemark.html)}</div>
        <Controls>
          <Search
            type="is-open"
            placeholder={inputField}
            value={input}
            arialabel={inputLabel}
            onChange={e => {
              setInput(e.target.value);
            }}
            onSubmit={getCoordinates}
            ref={inputRef}
          />
          <LocationButton onClick={getUserLocation}>
            <FormattedMessage id="myLocation" />
          </LocationButton>
        </Controls>
        {error && (
          <ErrorText>
            <FormattedMessage id="noSearchFound" />
          </ErrorText>
        )}
      </Wrapper>
      <MapContainer>
        <Map zoom={zoom} center={location} onLoad={onLoad} />
      </MapContainer>
      {!!closestLocations.length && (
        <MapResults
          className="no-bullets"
          type="A"
          role="status"
          aria-live="polite"
        >
          <p className="aria-location">
            {`${closestLocations.length} ${intl.formatMessage({
              id: 'locationFound',
            })}`}
          </p>
          {closestLocations.map((info, index) => {
            return (
              <li key={uuid()}>
                <ABMAccordion
                  data={info}
                  lang={intl.locale}
                  char={markerLabels[index]}
                />
              </li>
            );
          })}
        </MapResults>
      )}
    </Container>
  );
};

ABMLocator.propTypes = {
  data: PropTypes.shape().isRequired,
};

ABMAccordion.propTypes = {
  data: PropTypes.shape().isRequired,
  char: PropTypes.string,
  lang: PropTypes.string.isRequired,
};

export default ABMLocator;
